import type { FC, PropsWithChildren } from 'react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

type CacheData = {
  data: unknown;
  expires?: number;
};

type State = {
  getValue: <T>(key: string) => T | null | undefined;
  setValue: (key: string, value: unknown, options?: { ttl: number }) => void;
};

const CacheContext = createContext<State>({
  getValue: () => {
    throw new Error('CacheProvider not found');
  },
  setValue: () => {
    throw new Error('CacheProvider not found');
  },
});

CacheContext.displayName = 'CacheContext';

const useCacheContext = () => useContext(CacheContext);

const CacheProvider: FC<PropsWithChildren> = ({ children }) => {
  const cache = useMemo(() => new Map<string, CacheData>(), []);

  const getValue: State['getValue'] = useCallback(
    <T,>(key: string) => {
      const cached = cache.get(key);
      if (cached) {
        if (!cached.expires || cached.expires > Date.now()) {
          return cached.data as T;
        }
        cache.delete(key);
      }
      return null;
    },
    [cache],
  );

  const setValue: State['setValue'] = useCallback(
    (key, value, options) => {
      cache.set(key, {
        data: value,
        expires: options?.ttl ? Date.now() + options?.ttl : undefined,
      });
    },
    [cache],
  );

  useEffect(() => {
    const interval = setInterval(
      () => {
        const now = Date.now();
        for (const [key, value] of cache.entries()) {
          if (value.expires && value.expires < now) {
            cache.delete(key);
          }
        }
      },
      1000 * 60 * 30,
    );

    return () => {
      clearInterval(interval);
      cache.clear();
    };
  }, [cache]);

  const value = useMemo(() => ({ getValue, setValue }), [getValue, setValue]);

  return (
    <CacheContext.Provider value={value}>{children}</CacheContext.Provider>
  );
};

export { CacheProvider, useCacheContext };
